import React, { useEffect } from 'react';
import { injectedComponent } from '@base/core';
import { AUTH_TOKEN_STORAGE_KEY } from '@base/constants/authStorage';
import {
  buildVkIdRedirectUri,
  clearVkOAuthFlowMode,
  clearVkOAuthPkceSession,
  consumeVkOAuthLinkReturnUrl,
  extractVkIdAuthToken,
  getVkIdTokenExchangeUrl,
  isVkOAuthLinkFlow,
  readVkOAuthPkceSession,
  replaceLocationWithVkPanelToken,
} from '@base/utils/utils';
export default injectedComponent(
  {
    name: 'VkCallbackPage',
    services: ['api'],
  },
  function VkCallbackPage({ services: { api } }) {
    const redirectToProfileDialog = () => {
      const lang =
        (typeof localStorage !== 'undefined' && localStorage.getItem('currentLangId')) || 'ru';
      window.location.replace(`/${lang}/my/?dialog=profile`);
    };

    const redirectToSavedReturnUrlOrProfile = (errorCode = null) => {
      const returnUrl = consumeVkOAuthLinkReturnUrl();
      if (returnUrl) {
        const preparedUrl = new URL(returnUrl, window.location.origin);
        if (errorCode) {
          preparedUrl.searchParams.set('error', errorCode);
        }
        window.location.replace(`${preparedUrl.pathname}${preparedUrl.search}${preparedUrl.hash}`);
        return;
      }
      if (errorCode) {
        const lang =
          (typeof localStorage !== 'undefined' && localStorage.getItem('currentLangId')) || 'ru';
        window.location.replace(`/${lang}/my/?dialog=profile&error=${encodeURIComponent(errorCode)}`);
        return;
      }
      redirectToProfileDialog();
    };

    const redirectAfterVkCallbackFailure = () => {
      if (isVkOAuthLinkFlow()) {
        clearVkOAuthFlowMode();
        redirectToSavedReturnUrlOrProfile();
        return;
      }

      window.location.href = '/login';
    };

    const completeVkIdLinkFlow = async ({ accessToken }) => {
      const authToken = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
      try {
        const result = await api.linkVkId({
          payload: { accessToken },
          options: authToken ? { authToken } : {},
          responseType: 'full',
        });
        const linkError = result?.data?.error || null;
        clearVkOAuthFlowMode();
        redirectToSavedReturnUrlOrProfile(linkError);
      } catch {
        clearVkOAuthFlowMode();
        redirectToSavedReturnUrlOrProfile('linkSocialAccount');
      }
    };

    const handleExchangeCodeToToken = async ({ code, codeVerifier, deviceId }) => {
      try {
        const requestBody = {
          grant_type: 'authorization_code',
          code,
          code_verifier: codeVerifier,
          redirect_uri: buildVkIdRedirectUri(api.config),
          client_id: `${api.config.appId}`,
          device_id: deviceId,
        };

        const response = await fetch(getVkIdTokenExchangeUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams(requestBody),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(`OAuth error: ${JSON.stringify(data)}`);
        }

        const { access_token } = data;

        if (!access_token) {
          return;
        }

        const linkFlow = isVkOAuthLinkFlow();
        if (linkFlow) {
          await completeVkIdLinkFlow({ accessToken: access_token });
          return;
        }

        await sendTokensToBackend({ accessToken: access_token });

      } catch {
        redirectAfterVkCallbackFailure();
      }
    };

    const sendTokensToBackend = async ({ accessToken }) => {
      try {
        const storedAuthToken = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
        const data = await api.authVkId({
          payload: { accessToken },
          options: {
            noAuthToken: !storedAuthToken,
            ...(storedAuthToken ? { authToken: storedAuthToken } : {}),
          },
          responseType: 'full',
        });

        if (data.result === 'error') {
          throw new Error(data.data?.error || 'Unknown backend error');
        }

        const token = extractVkIdAuthToken(data);
        if (!token) {
          throw new Error('Token not received from backend');
        }

        localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
        api.updateState({ token });
        replaceLocationWithVkPanelToken(token);
      } catch {
        window.location.href = '/login';
      }
    };
    useEffect(() => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const receivedState = urlParams.get('state');
      const receivedDeviceId = urlParams.get('device_id');
      const error = urlParams.get('error');
      if (error) {
        redirectAfterVkCallbackFailure();
        return;
      }

      if (!code) {
        redirectAfterVkCallbackFailure();
        return;
      }

      const pkce = readVkOAuthPkceSession();
      if (!pkce) {
        redirectAfterVkCallbackFailure();
        return;
      }

      const { state: storedState, codeVerifier: storedCodeVerifier, deviceId: storedDeviceId } = pkce;

      if (receivedState !== storedState) {
        clearVkOAuthPkceSession();
        redirectAfterVkCallbackFailure();
        return;
      }

      const finalDeviceId = receivedDeviceId || storedDeviceId;

      clearVkOAuthPkceSession();

      handleExchangeCodeToToken({
        code,
        codeVerifier: storedCodeVerifier,
        deviceId: finalDeviceId,
      }).catch(() => {
        redirectAfterVkCallbackFailure();
      });
    }, []);

    return <></>;
  },
);
